import { Injectable, EventEmitter } from "@angular/core";
import { formatDate } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import {
  DataArray,
  SignalRService,
  SignalREventService,
  SurveySettings,
  SurveyCompany,
  SurveyDocument,
  SurveyMinesite,
  SurveyUser,
  SurveyStatus,
  User,
  Language,
  AuthorizationService,
} from "../index";
import { SurveyConflictResolution } from "../model/surveyconflictresolution.model";

@Injectable()
export class SurveyService {
  //#region Event Emitters

  public surveySettingsChange: EventEmitter<SurveySettings[]> =
    new EventEmitter<SurveySettings[]>();
  public surveyDocumentHistoryChange: EventEmitter<SurveyDocument[]> =
    new EventEmitter<SurveyDocument[]>();
  public surveyCompaniesChange: EventEmitter<SurveyCompany[]> =
    new EventEmitter<SurveyCompany[]>();
  public surveyMinesitesChange: EventEmitter<SurveyMinesite[]> =
    new EventEmitter<SurveyMinesite[]>();
  public surveyUsersChange: EventEmitter<SurveyUser[]> = new EventEmitter<
    SurveyUser[]
  >();

  public surveySettingsCreated: EventEmitter<SurveySettings> =
    new EventEmitter<SurveySettings>();
  public surveySettingsUpdated: EventEmitter<SurveySettings> =
    new EventEmitter<SurveySettings>();
  public surveySettingsDeleted: EventEmitter<SurveySettings> =
    new EventEmitter<SurveySettings>();
  public surveySettingsDeactivated: EventEmitter<SurveySettings> =
    new EventEmitter<SurveySettings>();
  public surveySettingsEnabled: EventEmitter<SurveySettings> =
    new EventEmitter<SurveySettings>();
  public surveyDocumentCreated: EventEmitter<SurveyDocument> =
    new EventEmitter<SurveyDocument>();
  public languageChange: EventEmitter<Language[]> = new EventEmitter<
    Language[]
  >();

  public pdfAzureError: EventEmitter<string> = new EventEmitter<string>();
  public pdfAzureChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public currentSurveySettingsChange: EventEmitter<SurveySettings> =
    new EventEmitter<SurveySettings>();
  public getSurveySettingsByIdError: EventEmitter<string> =
    new EventEmitter<string>();

  public surveyStatusesChange: EventEmitter<SurveyStatus[]> = new EventEmitter<
    SurveyStatus[]
  >();

  public warnAboutUnlinkingUsersWithCompletedSurveys: EventEmitter<User[]> =
    new EventEmitter<User[]>();
  public warnAboutSurveyConflicts: EventEmitter<SurveyConflictResolution[]> =
    new EventEmitter<SurveyConflictResolution[]>();
  public warnAboutSurveyConflictsForEnable: EventEmitter<
    SurveyConflictResolution[]
  > = new EventEmitter<SurveyConflictResolution[]>();

  //#endregion

  //#region Properties

  public surveySettings: DataArray<SurveySettings> =
    new DataArray<SurveySettings>();
  public currentSurveySettings: SurveySettings = new SurveySettings();
  public surveyDocumentHistory: DataArray<SurveyDocument> =
    new DataArray<SurveyDocument>();

  public surveyCompanies: DataArray<SurveyCompany> =
    new DataArray<SurveyCompany>();
  public surveyMinesites: DataArray<SurveyMinesite> =
    new DataArray<SurveyMinesite>();
  public surveyUsers: DataArray<SurveyUser> = new DataArray<SurveyUser>();

  public surveyStatuses: DataArray<SurveyStatus> =
    new DataArray<SurveyStatus>();

  public usersWhoHaveCompletedSurvey: DataArray<User> = new DataArray<User>();
  public languages: DataArray<Language> = new DataArray<Language>();

  public surveyConflicts: DataArray<SurveyConflictResolution> =
    new DataArray<SurveyConflictResolution>();

  public totalSurveys = 0;

  //#endregion

  constructor(
    private _signalRService: SignalRService,
    private _signalREventService: SignalREventService,
    private _authorizationService: AuthorizationService,
    private _http: HttpClient
  ) {
    this.registerOnServerEvents();
  }

  //#region Controller calls

  public uploadPDF(
    inFile: FormData,
    uploadProManual: boolean,
    surveyDocumentPkid: number
  ): void {
    let headers = new HttpHeaders();
    let options = { headers: headers };

    // upload file to Komatsu's Azure Storage
    // to-do: need to update API call to new one
    if (uploadProManual) {
      try {
        this._http
          .post(
            window.document.location.origin +
              "/api/Surveys/UploadSurveyPDFtoAzure?documentId=" +
              surveyDocumentPkid,
            inFile,
            options
          )
          .subscribe(
            (data) => {
              this.pdfAzureChange.emit(true);
            },
            (error) => {
              this.pdfAzureError.emit(error);
            },
            () => {
              console.log("upload completed to Azure");
            }
          );
      } catch (e) {
        this.pdfAzureError.emit(e.message);
      }
    }
  }

  public getSurveySettingsById(surveyId: number) {
    let headers = new HttpHeaders();
    let options = { headers: headers };

    try {
      this._http
        .get(
          window.document.location.origin +
            "/api/Surveys/getSurvey?surveyid=" +
            surveyId.toString(),
          options
        )
        .subscribe(
          (data) => {
            // to-do implement when controller is completed
            this.currentSurveySettingsChange.emit(this.currentSurveySettings);
          },
          (error) => {
            this.getSurveySettingsByIdError.emit(error);
          },
          () => {
            console.log("Current Survey Settings retrieved.");
          }
        );
    } catch (e) {
      this.getSurveySettingsByIdError.emit(e.message);
    }
  }

  // Error handling
  private handleError(error: any): Observable<never> {
    console.error("Error downloading PDF:", error);
    return throwError("Failed to download PDF. Please try again later.");
  }

  public downloadSurveyPDF(surveyId: number): Observable<Blob> {
    let headers = new HttpHeaders();
    headers = headers.append(
      "Authorization",
      `Bearer ${this._authorizationService.getAuthorization()}`
    );

    const url = `${window.document.location.origin}/api/surveys/DownloadSurvey?surveyid=${surveyId}`;
    return this._http
      .get(url, {
        headers: headers,
        responseType: "blob",
      })
      .pipe(catchError(this.handleError));
  }

  //#endregion

  //#region SignalR Events Invocations
  public getAllSurveySettings(
    filterApplied: boolean,
    orderBy: string,
    ascending: boolean,
    title: string,
    language: string,
    allowIgnore: boolean,
    isActive: boolean,
    createdBy: string,
    startDate: Date | null,
    expiresDate: Date | null
  ): void {
    let strStartDate = startDate ? startDate.toISOString() : "";
    let strExpiresDate = expiresDate ? expiresDate.toISOString() : "";

    this._signalRService.invoke(
      "FilterSurveySettings",
      filterApplied,
      orderBy,
      ascending,
      title,
      language,
      allowIgnore,
      isActive,
      createdBy,
      strStartDate,
      strExpiresDate
    );
  }

  public createSurveySettings(
    surveySettings: SurveySettings,
    currentUserId: number
  ): void {
    this._signalRService.invoke(
      "CreateSurveySettings",
      surveySettings.Title,
      surveySettings.Description,
      surveySettings.Message,
      surveySettings.StartDate,
      surveySettings.ExpiresDate,
      surveySettings.Language,
      surveySettings.AllowIgnore,
      surveySettings.IsActive,
      surveySettings.FileMetadataId,
      currentUserId,
      surveySettings.SurveyAssignment,
      true
    );
  }

  public updateSurveySettings(
    surveySettings: SurveySettings,
    currentUserId: number
  ): void {
    this._signalRService.invoke(
      "UpdateSurveySettings",
      surveySettings.Pkid,
      surveySettings.Title,
      surveySettings.Description,
      surveySettings.Message,
      surveySettings.StartDate,
      surveySettings.ExpiresDate,
      surveySettings.Language,
      surveySettings.AllowIgnore,
      surveySettings.IsActive,
      surveySettings.FileMetadataId,
      currentUserId,
      surveySettings.SurveyAssignment,
      true
    );
  }

  public deleteSurveySettings(pkid: number, userId: number): void {
    this._signalRService.invoke("DeleteSurveySettings", pkid, userId);
  }

  public disableSurveySettings(pkid: number, userId): void {
    this._signalRService.invoke("DisableSurveySettings", pkid, userId);
  }

  public enableSurveySettings(pkid: number, userId): void {
    this._signalRService.invoke("EnableSurveySettings", pkid, userId);
  }

  public applyFilterToSurveySettings(
    filterApplied: boolean,
    orderBy: string,
    ascending: boolean,
    title: string,
    language: string,
    allowIgnore: boolean,
    isActive: boolean,
    createdBy: string,
    startDate: Date | null,
    expiresDate: Date | null
  ): void {
    let strStartDate = startDate ? startDate.toISOString() : "";
    let strExpiresDate = expiresDate ? expiresDate.toISOString() : "";

    this._signalRService.invoke(
      "FilterSurveySettings",
      filterApplied,
      orderBy,
      ascending,
      title,
      language,
      allowIgnore,
      isActive,
      createdBy,
      strStartDate,
      strExpiresDate
    );
  }

  public createSurveyDocument(
    fileName: string,
    surveyId: number,
    fileMetadataId: number | null,
    currentUserId: number
  ): void {
    this._signalRService.invoke(
      "CreateSurveyDocument",
      fileName,
      surveyId,
      fileMetadataId,
      currentUserId
    );
  }

  public getSurveyDocumentHistory(fileMetadataId: number | null): void {
    this._signalRService.invoke("GetSurveyDocumentHistory", fileMetadataId);
  }

  public linkOrUnlinkCompaniesToSurvey(
    surveyId: number,
    companyIds: number[]
  ): void {
    this._signalRService.invoke(
      "LinkOrUnlinkCompaniesToSurvey",
      surveyId,
      companyIds
    );
  }

  public linkOrUnlinkMinesitesToSurvey(
    surveyId: number,
    minesiteIds: number[]
  ): void {
    this._signalRService.invoke(
      "LinkOrUnlinkMinesitesToSurvey",
      surveyId,
      minesiteIds
    );
  }

  public linkOrUnlinkUsersToSurvey(
    surveyId: number,
    userIds: number[],
    force: boolean
  ): void {
    this._signalRService.invoke(
      "LinkOrUnlinkUsersToSurvey",
      surveyId,
      userIds,
      force
    );
  }

  public getSurveyCompanies(surveyId: number): void {
    this._signalRService.invoke("GetSurveyCompanies", surveyId);
  }

  public getSurveyMinesites(surveyId: number): void {
    this._signalRService.invoke("GetSurveyMinesites", surveyId);
  }

  public getSurveyUsers(surveyId: number): void {
    this._signalRService.invoke("GetSurveyUsers", surveyId);
  }

  public getSurveyStatusesBySurveyId(surveyId: number): void {
    this._signalRService.invoke("GetSurveyStatusesBySurveyId", surveyId);
  }

  public getLanguages(): void {
    this._signalRService.invoke("GetLanguages");
  }

  public resolveSurveyConflicts(
    isCreate: boolean,
    currentSurvey: SurveySettings,
    currentUserId: number
  ): void {
    this._signalRService.invoke(
      "ResolveSurveyConflicts",
      this.surveyConflicts.Data,
      isCreate,
      currentSurvey,
      currentUserId
    );
  }

  //#endregion

  private calculateDateStringsForSurveySettings(
    surveySettingsToModify: SurveySettings
  ): SurveySettings {
    try {
      if (surveySettingsToModify.CreatedOn != null) {
        surveySettingsToModify.CreatedOnString = formatDate(
          surveySettingsToModify.CreatedOn,
          "yyyy-MM-dd'T'HH:mm",
          "en-US"
        );
      }

      if (surveySettingsToModify.ModifiedOn != null) {
        surveySettingsToModify.ModifiedOnString = formatDate(
          surveySettingsToModify.ModifiedOn,
          "yyyy-MM-dd'T'HH:mm",
          "en-US"
        );
      }
    } catch (exception) {
      console.error("CreatedOn or ModifiedOn dates are not valid.");
    } finally {
      return surveySettingsToModify;
    }
  }

  //#region SignalR Events Receiving
  private onGetAllSurveySettings(info: any): void {
    //@ts-ignore
    this.totalSurveys = info.Data.Total;
    this.surveySettings.init();

    //@ts-ignore
    for (const item of info.Data.SurveySettings) {
      let currentSurveySettings: SurveySettings = $.extend(
        new SurveySettings(),
        item
      );
      currentSurveySettings = this.calculateDateStringsForSurveySettings(
        currentSurveySettings
      );
      this.surveySettings.push(currentSurveySettings);
    }

    this.surveySettingsChange.emit(this.surveySettings.Data);
  }

  private onCreateSurveySettings(info: any): void {
    //@ts-ignore
    let surveySettingsCreated: SurveySettings = $.extend(
      new SurveySettings(),
      info.Data.NewSurveySettings
    );
    surveySettingsCreated = this.calculateDateStringsForSurveySettings(
      surveySettingsCreated
    );
    if (this.surveySettings.update(surveySettingsCreated, true)) {
      this.surveySettingsCreated.emit(surveySettingsCreated);
    }
  }

  private onUpdateSurveySettings(info: any): void {
    //@ts-ignore
    let surveySettingsUpdated: SurveySettings = $.extend(
      new SurveySettings(),
      info.Data.SavedSurveySettings
    );
    surveySettingsUpdated = this.calculateDateStringsForSurveySettings(
      surveySettingsUpdated
    );
    this.surveySettingsUpdated.emit(surveySettingsUpdated);
  }

  private onDisableSurveySettings(info: any): void {
    //@ts-ignore
    const survey: SurveySettings = $.extend(
      new SurveySettings(),
      info.Data.InactivatedSurveySettings
    );
    this.surveySettingsDeactivated.emit(survey);
  }

  private onEnableSurveySettings(info: any): void {
    //@ts-ignore
    const survey: SurveySettings = $.extend(
      new SurveySettings(),
      info.Data.ActivatedSurveySettings
    );
    this.surveySettingsEnabled.emit(survey);
  }

  private onDeleteSurveySettings(info: any): void {
    //@ts-ignore
    const survey: SurveySettings = $.extend(
      new SurveySettings(),
      info.Data.DeletedSurveySettings
    );
    this.surveySettingsDeleted.emit(survey);
  }

  private onCreateSurveyDocument(info: any): void {
    //@ts-ignore
    const surveyDocument: SurveyDocument = $.extend(
      new SurveyDocument(),
      info.Data.SavedSurveyDocument
    );
    this.surveyDocumentCreated.emit(surveyDocument);
  }

  private onGetSurveyDocumentHistory(info: any): void {
    this.surveyDocumentHistory.init();

    //@ts-ignore
    for (const item of info.Data.SurveyDocumentHistory) {
      const surveyDocument: SurveyDocument = $.extend(
        new SurveyDocument(),
        item
      );
      this.surveyDocumentHistory.push(surveyDocument);
    }

    this.surveyDocumentHistoryChange.emit(this.surveyDocumentHistory.Data);
  }

  private onGetSurveyCompanies(info: any): void {
    this.surveyCompanies.init();

    //@ts-ignore
    for (const item of info.Data.SurveyCompanies) {
      const surveyCompany: SurveyCompany = $.extend(new SurveyCompany(), item);
      this.surveyCompanies.push(surveyCompany);
    }
    this.surveyCompaniesChange.emit(this.surveyCompanies.Data);
  }

  private onGetSurveyMinesites(info: any): void {
    this.surveyMinesites.init();

    //@ts-ignore
    for (const item of info.Data.SurveyMinesites) {
      const surveyMinesite: SurveyMinesite = $.extend(
        new SurveyMinesite(),
        item
      );
      this.surveyMinesites.push(surveyMinesite);
    }

    this.surveyMinesitesChange.emit(this.surveyMinesites.Data);
  }

  private onGetSurveyUsers(info: any): void {
    this.surveyUsers.init();

    //@ts-ignore
    for (const item of info.Data.SurveyUsers) {
      const surveyUser: SurveyUser = $.extend(new SurveyUser(), item);
      this.surveyUsers.push(surveyUser);
    }

    this.surveyUsersChange.emit(this.surveyUsers.Data);
  }

  private onGetSurveyStatusesBySurveyId(info: any): void {
    this.surveyStatuses.init();

    //@ts-ignore
    for (const item of info.Data.SurveyStatuses) {
      const surveyStatus: SurveyStatus = $.extend(new SurveyStatus(), item);
      this.surveyStatuses.push(surveyStatus);
    }

    this.surveyStatusesChange.emit(this.surveyStatuses.Data);
  }

  private onWarnAdminAboutUnlinkingUsers(info: any): void {
    this.usersWhoHaveCompletedSurvey.init();
    //@ts-ignore
    for (const item of info.Data.UsersWhoHaveCompletedSurvey) {
      const user: User = $.extend(new User(), item);
      this.usersWhoHaveCompletedSurvey.push(user);
    }

    this.warnAboutUnlinkingUsersWithCompletedSurveys.emit(
      this.usersWhoHaveCompletedSurvey.Data
    );
  }

  private onLanguages(info: any): void {
    this.languages.init();
    for (const item of info.Data.Languages) {
      const language: Language = $.extend(new Language(), item);
      this.languages.push(language);
    }

    this.languageChange.emit(this.languages.Data);
  }

  private onSurveyConflictsFound(info: any): void {
    this.surveyConflicts.init();
    //@ts-ignore
    for (const item of info.Data.SurveyConflictResolutions) {
      const surveyConflict: SurveyConflictResolution = $.extend(
        new SurveyConflictResolution(),
        item
      );
      this.surveyConflicts.push(surveyConflict);
    }

    this.warnAboutSurveyConflicts.emit(this.surveyConflicts.Data);
  }

  private onSurveyConflictsFoundBeforeEnable(info: any): void {
    this.surveyConflicts.init();
    //@ts-ignore
    for (const item of info.Data.SurveyConflictResolutions) {
      const surveyConflict: SurveyConflictResolution = $.extend(
        new SurveyConflictResolution(),
        item
      );
      this.surveyConflicts.push(surveyConflict);
    }

    this.warnAboutSurveyConflictsForEnable.emit(this.surveyConflicts.Data);
  }
  //#endregion

  private registerOnServerEvents() {
    this._signalREventService.register(
      "SurveyService",
      "getAllSurveySettings",
      (info: any) => this.onGetAllSurveySettings(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "createdSurveySettings",
      (info: any) => this.onCreateSurveySettings(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "updatedSurveySettings",
      (info: any) => this.onUpdateSurveySettings(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "deletedSurveySettings",
      (info: any) => this.onDeleteSurveySettings(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "disabledSurveySettings",
      (info: any) => this.onDisableSurveySettings(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "enabledSurveySettings",
      (info: any) => this.onEnableSurveySettings(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "filteredSurveySettings",
      (info: any) => this.onGetAllSurveySettings(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "createdSurveyDocument",
      (info: any) => this.onCreateSurveyDocument(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "getSurveyDocumentHistory",
      (info: any) => this.onGetSurveyDocumentHistory(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "getSurveyCompanies",
      (info: any) => this.onGetSurveyCompanies(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "getSurveyMinesites",
      (info: any) => this.onGetSurveyMinesites(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "getSurveyUsers",
      (info: any) => this.onGetSurveyUsers(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "getSurveyStatusesBySurveyId",
      (info: any) => this.onGetSurveyStatusesBySurveyId(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "warnAdminAboutUnlinkingUsers",
      (info: any) => this.onWarnAdminAboutUnlinkingUsers(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "languages",
      (info: any) => this.onLanguages(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "surveyConflictsFound",
      (info: any) => this.onSurveyConflictsFound(info)
    );
    this._signalREventService.register(
      "SurveyService",
      "surveyConflictsFoundBeforeEnable",
      (info: any) => this.onSurveyConflictsFoundBeforeEnable(info)
    );
  }

  private deregisterOnServerEvents(): void {
    this._signalREventService.deregister("SurveyService");
  }
}
